import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  CContainer,
  CCard,
  CCardHeader,
  CCardBody,
  CSpinner,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
  COffcanvas,
  COffcanvasBody
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilMediaPlay, cilSettings, cilMap, cilFolderOpen } from "@coreui/icons";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ru from "date-fns/locale/ru";

import CameraHeader from "./components/CameraHeader";
import CameraFilterPanel from "./components/CameraFilterPanel";
import CameraVideoPlayer from "./components/CameraVideoPlayer";
import CameraArchiveList from "./components/CameraArchiveList";
import CameraTabs from "./components/CameraTabs";
import CameraModals from "./components/CameraModals";
import GarbageTruckDashboard from "./components/GarbageTruckDashboard";
import GarbageTimelineSection from "./components/GarbageTimelineSection";
import GPSMap from "../../components/GPSMap/GPSMap";

import { fromFakeLocalToUtcIso, getFakeLocalDate, getDeviceOffsetMin } from "./utils/timeUtils";
import { handleExportGarbageExcel } from "./utils/exportGarbageUtils";
import "./CameraDetailPage.css";

registerLocale("ru", ru);

const VIDEO_FPS = 24.0;

const videoSecToDbMs = (sec, mapping, fallbackStartMs) => {
  if (!mapping || mapping.length === 0) return fallbackStartMs + sec * 1000;
  const targetF = sec * VIDEO_FPS;
  let l = 0, r = mapping.length - 1;
  while (l <= r) {
    const m = Math.floor((l + r) / 2);
    if (mapping[m].f === targetF) return new Date(mapping[m].t).getTime();
    if (mapping[m].f < targetF) l = m + 1;
    else r = m - 1;
  }
  if (l >= mapping.length) return new Date(mapping[mapping.length - 1].t).getTime();
  if (r < 0) return new Date(mapping[0].t).getTime();
  const diffL = Math.abs(mapping[l].f - targetF);
  const diffR = Math.abs(mapping[r].f - targetF);
  return new Date(diffL < diffR ? mapping[l].t : mapping[r].t).getTime();
};

const GARBAGE_CLASS_COLORS = {
  u00001: "#00e1ff",
  u00002: "#58ff4d",
  u00003: "#ff0000",
  u00004: "#ffd500",
  u00005: "#ff9500",
  u00007: "#8cff00",
  u00008: "#9e9e9e",
};

const GARBAGE_CLASS_LABELS = {
  u00001: "Площадка КП",
  u00002: "Пустой бак",
  u00003: "Полный бак",
  u00004: "КГО",
  u00005: "Навал ТКО",
  u00007: "Погрузка бака",
  u00008: "Дефект бака",
};

const GarbageTruckDetailPage = () => {
  const { t } = useTranslation();
  const { cameraId } = useParams();
  const isSuperuser = localStorage.getItem("is_superuser") === "true";
  const isStaff = localStorage.getItem("is_staff") === "true" || isSuperuser;

  const [device, setDevice] = useState(null);
  const [folders, setFolders] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [currentFolderStartTime, setCurrentFolderStartTime] = useState(null);
  const [currentAbsoluteTime, setCurrentAbsoluteTime] = useState(null);
  const [clickedTimeMs, setClickedTimeMs] = useState(null);

  const [archiveRightPanelMode, setArchiveRightPanelMode] = useState("map");

  const [analyticsData, setAnalyticsData] = useState(null);
  const [trajectoryData, setTrajectoryData] = useState([]);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [analyticsCooldown, setAnalyticsCooldown] = useState(0);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [datePreset, setDatePreset] = useState("today");
  const [datesInitialized, setDatesInitialized] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  const [activeSettingsTab, setActiveSettingsTab] = useState("settings");
  const [schedule, setSchedule] = useState({
    shift_start: "00:00",
    shift_end: "23:59",
    work_days: 127,
    timezone_offset: 0,
    max_cycle_time_sec: 180,
    downtime_threshold_sec: 300,
    has_multiple_shifts: false,
    realtime_stream_enabled: false,
  });
  const [savingSchedule, setSavingSchedule] = useState(false);

  const [shifts, setShifts] = useState([]);
  const [newShift, setNewShift] = useState({ name: "Дневная", start_time: "08:00", end_time: "20:00" });
  const [breaks, setBreaks] = useState([]);
  const [newBreak, setNewBreak] = useState({ start_time: "12:00", end_time: "13:00", description: "Обед" });
  const [exceptions, setExceptions] = useState([]);
  const [newException, setNewException] = useState({
    startDate: new Date(), endDate: new Date(), is_working: false, is_maintenance: false, description: ""
  });
  const [materials, setMaterials] = useState([]);
  const [newMaterial, setNewMaterial] = useState({
    name: "ТКО", is_default: true, startDate: new Date(), endDate: new Date(), bulk_density: 300, true_density: 500, fill_factor: 1.0, loosening_factor: 1.0
  });

  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [offcanvasVisible, setOffcanvasVisible] = useState(false);
  const [recalcModalVisible, setRecalcModalVisible] = useState(false);
  const [recalcStartDate, setRecalcStartDate] = useState(null);
  const [recalculating, setRecalculating] = useState(false);

  // --- СТЕЙТЫ ДЛЯ ФОРМАТИРОВАНИЯ (СКРЫТИЯ / ВОССТАНОВЛЕНИЯ ДАННЫХ) ---
  const [formatModalVisible, setFormatModalVisible] = useState(false);
  const [formatMode, setFormatMode] = useState("all");
  const [formatDate, setFormatDate] = useState(new Date());
  const [formatConfirmation, setFormatConfirmation] = useState("");
  const [formatting, setFormatting] = useState(false);
  const [formatActionTab, setFormatActionTab] = useState("hide");
  const [formatStats, setFormatStats] = useState(null);

  const openFormatModal = async () => {
    setFormatActionTab("hide");
    setFormatConfirmation("");
    setFormatModalVisible(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`/api/devices/${cameraId}/clear-data/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormatStats(res.data);
    } catch (e) {
      console.warn("Не удалось загрузить статус скрытых папок", e);
    }
  };

  const handleClearData = async () => {
    if (!device) return;
    setFormatting(true);
    try {
      const token = localStorage.getItem("token");
      const payload = { action: "hide", mode: formatMode };
      if (formatMode === "date") {
        payload.date = fromFakeLocalToUtcIso(formatDate, device?.timezone_offset || 0);
      }
      const response = await axios.post(`/api/devices/${cameraId}/clear-data/`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(response.data.message || "Данные успешно скрыты");
      setFormatModalVisible(false);
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка при скрытии данных");
    } finally {
      setFormatting(false);
    }
  };

  const handleRestoreData = async () => {
    if (!device) return;
    setFormatting(true);
    try {
      const token = localStorage.getItem("token");
      const payload = { action: "restore", mode: formatMode };
      if (formatMode === "date") {
        payload.date = fromFakeLocalToUtcIso(formatDate, device?.timezone_offset || 0);
      }
      const response = await axios.post(`/api/devices/${cameraId}/clear-data/`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(response.data.message || "Видимость успешно восстановлена");
      setFormatModalVisible(false);
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка при восстановлении данных");
    } finally {
      setFormatting(false);
    }
  };

  const [seekToTime, setSeekToTime] = useState(null);
  const [seekTriggerId, setSeekTriggerId] = useState(0);
  const customPlayerRef = useRef(null);
  const canvasRef = useRef(null);

  const [detections, setDetections] = useState({});
  const [showDetections, setShowDetections] = useState(true);
  const [framesMapping, setFramesMapping] = useState([]);
  const framesMappingRef = useRef([]);

  useEffect(() => {
    framesMappingRef.current = framesMapping;
  }, [framesMapping]);

  useEffect(() => {
    if (analyticsCooldown > 0) {
      const timer = setTimeout(() => setAnalyticsCooldown((prev) => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [analyticsCooldown]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    const loadInit = async () => {
      try {
        const [resDev, resFolders, resShifts, resBreaks, resDates] = await Promise.all([
          axios.get(`/api/devices/${cameraId}/`, { headers }),
          axios.get(`/api/devices/${cameraId}/folders/`, { headers }),
          axios.get(`/api/devices/${cameraId}/shifts/`, { headers }).catch(() => ({ data: [] })),
          axios.get(`/api/devices/${cameraId}/breaks/`, { headers }).catch(() => ({ data: [] })),
          axios.get(`/api/devices/${cameraId}/available-dates/`, { headers }).catch(() => ({ data: [] })),
        ]);

        const devData = resDev.data;
        setDevice(devData);
        setFolders(resFolders.data || []);
        setShifts(Array.isArray(resShifts.data) ? resShifts.data : resShifts.data.results || []);
        setBreaks(Array.isArray(resBreaks.data) ? resBreaks.data : resBreaks.data.results || []);

        if (resDates.data) {
          setAvailableDates(resDates.data.map((d) => new Date(d)));
        }

        setSchedule({
          shift_start: devData.shift_start ? devData.shift_start.substring(0, 5) : "00:00",
          shift_end: devData.shift_end ? devData.shift_end.substring(0, 5) : "23:59",
          work_days: devData.work_days || 127,
          timezone_offset: devData.timezone_offset || 0,
          max_cycle_time_sec: devData.max_cycle_time_sec || 180,
          downtime_threshold_sec: devData.downtime_threshold_sec || 300,
          has_multiple_shifts: devData.has_multiple_shifts || false,
          realtime_stream_enabled: devData.realtime_stream_enabled || false,
        });

        const offsetMin = getDeviceOffsetMin(devData.timezone_offset || 0);
        const now = dayjs().utcOffset(offsetMin);
        const s = now.startOf("day").toDate();
        const e = now.endOf("day").toDate();
        setStartDate(s);
        setEndDate(e);
        setRecalcStartDate(s);
        setFormatDate(e);
        setDatesInitialized(true);

        fetchGarbageAnalytics(s, e, devData.timezone_offset || 0);
      } catch (err) {
        toast.error("Ошибка загрузки данных устройства");
      }
    };
    loadInit();
  }, [cameraId]);

  const fetchGarbageAnalytics = async (s = startDate, e = endDate, tz = device?.timezone_offset || 0) => {
    if (!s || !e) return;
    if (analyticsCooldown > 0) {
      toast.warn(`Подождите ${analyticsCooldown} сек.`);
      return;
    }
    setLoadingAnalytics(true);
    setTrajectoryData([]);
    try {
      const token = localStorage.getItem("token");
      const utcStart = fromFakeLocalToUtcIso(s, tz);
      const utcEnd = fromFakeLocalToUtcIso(e, tz);

      const res = await axios.get("/api/garbage-analytics/", {
        headers: { Authorization: `Bearer ${token}` },
        params: { device_id: cameraId, start_date: utcStart, end_date: utcEnd },
      });
      setAnalyticsData(res.data);

      try {
        const trajResponse = await axios.get(`/api/devices/${cameraId}/trajectory/`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { start_date: utcStart, end_date: utcEnd },
        });
        setTrajectoryData(trajResponse.data.map((pt) => ({ ...pt, ts: getFakeLocalDate(pt.ts, tz) })));
      } catch (err) {
        console.warn("Траектория GPS не найдена");
      }

    } catch (err) {
      toast.error("Ошибка получения аналитики мусоровоза");
    } finally {
      setLoadingAnalytics(false);
      if (!isSuperuser) setAnalyticsCooldown(5);
    }
  };

  const handleVideoSelect = (folder) => {
    if (!folder.video_file) return;
    const startUtc = dayjs(folder.process_after).subtract(1, "hour");
    setCurrentFolderStartTime(startUtc);
    setSelectedVideo(folder.video_file);
    setSelectedFolderId(folder.id);
    setSeekToTime(0);
    setSeekTriggerId(Date.now());
    setVideoModalVisible(true);
  };

  useEffect(() => {
    if (!selectedFolderId) {
      setDetections({});
      setFramesMapping([]);
      return;
    }
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    axios
      .get(`/api/devices/${cameraId}/folders/${selectedFolderId}/detections/`, { headers })
      .then((res) => setDetections(res.data))
      .catch(console.error);

    axios
      .get(`/api/folders/${selectedFolderId}/markup/`, { headers })
      .then((res) => setFramesMapping(res.data.frames_mapping || []))
      .catch(() => setFramesMapping([]));
  }, [selectedFolderId, cameraId]);

  useEffect(() => {
    let animId;
    let lastTimeUpdate = 0;

    const render = () => {
      const video = customPlayerRef.current?.getInternalPlayer();
      const canvas = canvasRef.current;

      if (video && video.readyState >= 2) {
        const now = Date.now();

        if (now - lastTimeUpdate > 250) {
          const fallbackMs = currentFolderStartTime ? currentFolderStartTime.valueOf() : Date.now();
          const currentDbMs = videoSecToDbMs(video.currentTime, framesMappingRef.current, fallbackMs);
          const localDate = getFakeLocalDate(new Date(currentDbMs).toISOString(), device?.timezone_offset || 0);

          setCurrentAbsoluteTime((prev) => {
            if (!prev || Math.abs(prev.getTime() - localDate.getTime()) > 250) {
              return localDate;
            }
            return prev;
          });
          lastTimeUpdate = now;
        }

        if (canvas) {
          const ctx = canvas.getContext("2d");
          const vw = video.videoWidth;
          const vh = video.videoHeight;
          if (canvas.width !== vw || canvas.height !== vh) {
            canvas.width = vw;
            canvas.height = vh;
          }
          ctx.clearRect(0, 0, canvas.width, canvas.height);

          if (showDetections) {
            const fIdx = Math.round(video.currentTime * VIDEO_FPS);
            const objs = detections[fIdx] || detections[fIdx - 1] || detections[fIdx + 1];
            if (objs && objs.length > 0) {
              objs.forEach(([cls, x1, y1, x2, y2, conf]) => {
                if (cls === "u00006" || cls === "u00009") return;

                const color = GARBAGE_CLASS_COLORS[cls] || "red";
                const sx = x1 * vw;
                const sy = y1 * vh;
                const sw = (x2 - x1) * vw;
                const sh = (y2 - y1) * vh;

                ctx.strokeStyle = color;
                ctx.lineWidth = 3;
                ctx.strokeRect(sx, sy, sw, sh);

                const label = `${GARBAGE_CLASS_LABELS[cls] || cls} ${(conf * 100).toFixed(0)}%`;
                ctx.fillStyle = color;
                ctx.fillRect(sx, sy - 20, ctx.measureText(label).width + 10, 20);
                ctx.fillStyle = "white";
                ctx.font = "bold 13px Arial";
                ctx.fillText(label, sx + 5, sy - 5);
              });
            }
          }
        }
      }
      animId = requestAnimationFrame(render);
    };
    render();
    return () => cancelAnimationFrame(animId);
  }, [detections, showDetections, currentFolderStartTime, device]);

  const handleTimeDoubleClick = async ({ exactTime }) => {
    if (!exactTime) return;
    setClickedTimeMs(new Date(exactTime).getTime());
    const fakeDate = new Date(exactTime);
    const utcIso = fromFakeLocalToUtcIso(fakeDate, device?.timezone_offset || 0);

    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`/api/devices/${cameraId}/video-frame/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { timestamp: utcIso },
      });
      const { video_url, seek_seconds, folder_id } = res.data;

      if (video_url !== selectedVideo) {
        setFramesMapping([]);
        const folder = folders.find((f) => f.id === folder_id);
        if (folder) {
          const startTimeUtc = dayjs(folder.process_after).subtract(1, "hour");
          setCurrentFolderStartTime(startTimeUtc);
        }
        setSelectedVideo(video_url);
        setSelectedFolderId(folder_id);
      }
      setSeekToTime(seek_seconds);
      setSeekTriggerId(Date.now());
      setCurrentAbsoluteTime(fakeDate);

      setVideoModalVisible(true);
      if (customPlayerRef.current) customPlayerRef.current.play();
    } catch (err) {
      toast.error("Не удалось найти видео для этого момента.");
    }
  };

  const handleRecalculateSubmit = async () => {
    setRecalculating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        "/api/analytics/recalculate/",
        {
          device_id: cameraId,
          start_date: fromFakeLocalToUtcIso(recalcStartDate, device?.timezone_offset || 0),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || "Пересчет запущен");
      setRecalcModalVisible(false);
      setTimeout(() => fetchGarbageAnalytics(), 2000);
    } catch (err) {
      toast.error("Ошибка при запуске пересчета");
    } finally {
      setRecalculating(false);
    }
  };

  const handleSaveSchedule = async () => {
    setSavingSchedule(true);
    try {
      const token = localStorage.getItem("token");
      await axios.patch(`/api/devices/${cameraId}/`, schedule, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Настройки успешно сохранены!");
    } catch (e) {
      toast.error("Ошибка сохранения настроек");
    } finally {
      setSavingSchedule(false);
    }
  };

  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    if (preset === "custom") return;

    const offsetMin = getDeviceOffsetMin(device?.timezone_offset || 0);
    const d = dayjs().utcOffset(offsetMin);
    const nowFake = new Date(d.year(), d.month(), d.date(), d.hour(), d.minute(), d.second());

    let newStart = new Date(nowFake);
    let newEnd = new Date(nowFake);
    newEnd.setHours(23, 59, 59, 999);

    switch (preset) {
      case "today": newStart.setHours(0, 0, 0, 0); break;
      case "yesterday":
        newStart.setDate(newStart.getDate() - 1);
        newStart.setHours(0, 0, 0, 0);
        newEnd = new Date(newStart);
        newEnd.setHours(23, 59, 59, 999);
        break;
      case "last_2_days": newStart.setDate(newStart.getDate() - 1); newStart.setHours(0, 0, 0, 0); break;
      case "last_3_days": newStart.setDate(newStart.getDate() - 2); newStart.setHours(0, 0, 0, 0); break;
      case "last_7_days": newStart.setDate(newStart.getDate() - 6); newStart.setHours(0, 0, 0, 0); break;
      case "last_14_days": newStart.setDate(newStart.getDate() - 13); newStart.setHours(0, 0, 0, 0); break;
      case "last_month": newStart.setMonth(newStart.getMonth() - 1); newStart.setHours(0, 0, 0, 0); break;
      default: return;
    }
    setStartDate(newStart);
    setEndDate(newEnd);
  };

  const triggerExportExcel = () => {
    handleExportGarbageExcel({
      analyticsData,
      startDate,
      endDate,
      device,
      setExportingExcel,
      toast
    });
  };

  const statusTimelineItems = useMemo(() => {
    if (!analyticsData?.timeline) return [];
    return analyticsData.timeline.map((item, idx) => {
      const startMs = new Date(item.start).getTime();
      const endMs = new Date(item.end).getTime();

      let color = "#adb5bd";
      let label = "Движение";

      if (item.status_code === 1) {
        color = "#00e1ff";
        label = "Площадка КП";
      } else if (item.status_code === 10) {
        color = "#e55353";
        label = "Простой";
      } else if (item.status_code === -1) {
        color = "#8a93a2";
        label = "Нет связи";
      }

      return {
        id: `status_${idx}`,
        start: startMs,
        end: endMs > startMs ? endMs : startMs + 1000,
        color,
        label,
        originalItem: item,
      };
    });
  }, [analyticsData]);

  const sitesTimelineItems = useMemo(() => {
    if (!analyticsData?.sites_summary || !analyticsData.date_range) return [];
    const result = [];

    const sortedSites = [...analyticsData.sites_summary].sort(
      (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
    );

    sortedSites.forEach((site, index) => {
      const sStart = new Date(site.start).getTime();
      const sEnd = new Date(site.end).getTime();

      let barColor = "#2eb85c";
      if (site.has_unemptied_containers) {
        barColor = "#e55353";
      } else if (site.has_overflow || site.has_bulky_waste || site.has_container_defect) {
        barColor = "#e65100";
      }

      const warnIcon = site.has_unemptied_containers ? " 🚨(Полные баки!)" : (site.has_overflow ? " ⚠️" : "");

      result.push({
        id: `site_${site.id}`,
        start: sStart,
        end: sEnd > sStart ? sEnd : sStart + 5000,
        color: barColor,
        label: `КП #${index + 1}: ${site.containers_count} бак.${warnIcon}`,
        originalItem: site,
      });
    });

    return result;
  }, [analyticsData]);

  const dateRangeForVis = useMemo(() => {
    if (!startDate || !endDate) return null;
    return { start: startDate, end: endDate };
  }, [startDate, endDate]);

  if (!datesInitialized || !device) return <div className="text-center p-5"><CSpinner /></div>;

  return (
    <div className="bg-light min-vh-100 p-2 p-md-4">
      <CContainer fluid className="dashboard-centered mobile-p-small">
        <CameraHeader deviceName={device.name} organizationName={device.organization_name} t={t} />

        <CCard className="mb-4 border-0 shadow-sm">
          <CCardHeader className="d-flex flex-wrap justify-content-between align-items-center bg-white border-bottom-0 p-3">
            <CameraFilterPanel
              t={t}
              isStaff={isStaff}
              isSuperuser={isSuperuser}
              filterMode="custom"
              setFilterMode={() => { }}
              datePreset={datePreset}
              handlePresetChange={handlePresetChange}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              availableDates={availableDates}
              currentLang="ru"
              fetchAnalytics={() => fetchGarbageAnalytics()}
              loadingAnalytics={loadingAnalytics}
              analyticsCooldown={analyticsCooldown}
              device={device}
              volumeMode="nominal"
              setVolumeMode={() => { }}
              hideVolumeMode={true}
              hideShiftMode={true}
              setRecalcStartDate={setRecalcStartDate}
              setRecalcModalVisible={setRecalcModalVisible}
              setFormatConfirmation={setFormatConfirmation}
              setFormatModalVisible={setFormatModalVisible}
              openFormatModal={openFormatModal}
              triggerExportExcel={triggerExportExcel}
              exportingExcel={exportingExcel}
              toast={toast}
            />
          </CCardHeader>

          <CCardBody>
            <GarbageTruckDashboard
              data={analyticsData}
              trajectoryData={trajectoryData}
              onVideoJump={(timeMs) => handleTimeDoubleClick({ exactTime: timeMs })}
              timelineComponent={
                <GarbageTimelineSection
                  dateRangeForVis={dateRangeForVis}
                  statusTimelineItems={statusTimelineItems}
                  sitesTimelineItems={sitesTimelineItems}
                  handleTimeDoubleClick={handleTimeDoubleClick}
                  currentTimeMs={currentAbsoluteTime ? currentAbsoluteTime.getTime() : clickedTimeMs}
                />
              }
            />
          </CCardBody>
        </CCard>

        {/* ПЛАВАЮЩИЕ КНОПКИ СПРАВА СНИЗУ */}
        <div className="floating-controls">
          <button
            className="floating-btn floating-btn-video"
            title="Видеоархив"
            onClick={() => setVideoModalVisible(true)}
          >
            <CIcon icon={cilMediaPlay} size="xl" />
          </button>
          <button
            className="floating-btn floating-btn-settings"
            title="Настройки, Телеметрия, Карта"
            onClick={() => setOffcanvasVisible(true)}
          >
            <CIcon icon={cilSettings} size="xl" />
          </button>
        </div>

        {/* ВЫЕЗЖАЮЩАЯ ПАНЕЛЬ НАСТРОЕК */}
        <COffcanvas
          placement="end"
          visible={offcanvasVisible}
          onHide={() => setOffcanvasVisible(false)}
          style={{ width: "30vw", minWidth: "400px", maxWidth: "100%", padding: 0, border: "none" }}
        >
          <COffcanvasBody className="p-0">
            <CameraTabs
              variant="garbage"
              activeTab={activeSettingsTab}
              setActiveTab={setActiveSettingsTab}
              schedule={schedule}
              handleScheduleChange={(e) => setSchedule({ ...schedule, [e.target.name]: e.target.value })}
              handleWorkDayChange={(i) => setSchedule({ ...schedule, work_days: schedule.work_days ^ (1 << i) })}
              savingSchedule={savingSchedule}
              handleSaveSchedule={handleSaveSchedule}
              shifts={shifts}
              newShift={newShift}
              setNewShift={setNewShift}
              handleAddShift={() => { }}
              handleDeleteShift={() => { }}
              breaks={breaks}
              newBreak={newBreak}
              setNewBreak={setNewBreak}
              handleAddBreak={() => { }}
              handleDeleteBreak={() => { }}
              exceptions={exceptions}
              newException={newException}
              setNewException={setNewException}
              handleAddException={() => { }}
              handleDeleteException={() => { }}
              materials={materials}
              newMaterial={newMaterial}
              setNewMaterial={setNewMaterial}
              handleAddMaterial={() => { }}
              handleDeleteMaterial={() => { }}
              trajectoryData={trajectoryData}
              currentAbsoluteTime={currentAbsoluteTime}
              telemetryData={null}
              analyticsData={analyticsData}
              cameraId={cameraId}
              isStaff={isStaff}
              isSuperuser={isSuperuser}
              onClose={() => setOffcanvasVisible(false)}
              onVideoJump={(timeMs) => handleTimeDoubleClick({ exactTime: timeMs })}
            />
          </COffcanvasBody>
        </COffcanvas>

        {/* МОДАЛЬНОЕ ОКНО ВИДЕОАРХИВА */}
        <CModal
          size="xl"
          alignment="center"
          className="video-archive-modal"
          visible={videoModalVisible}
          onClose={() => setVideoModalVisible(false)}
          fullscreen="xl"
        >
          <CModalHeader className="py-2 px-3">
            <CModalTitle className="fs-6 fw-bold">Видеоархив и Траектория</CModalTitle>
          </CModalHeader>
          <CModalBody className="bg-light p-2 d-flex flex-column">
            <div className="d-flex flex-column modal-body-inner">
              <div className="row g-2 modal-top-row align-items-stretch" style={{ minHeight: 0 }}>
                <div className="col-12 col-xl-8 d-flex flex-column modal-video-col">
                  <CameraVideoPlayer
                    t={t}
                    selectedVideo={selectedVideo}
                    showDetections={showDetections}
                    setShowDetections={setShowDetections}
                    setExportModalVisible={() => { }}
                    customPlayerRef={customPlayerRef}
                    seekToTime={seekToTime}
                    seekTriggerId={seekTriggerId}
                    canvasRef={canvasRef}
                  />
                </div>

                <div className="col-12 col-xl-4 d-flex flex-column modal-side-col">
                  <div className="d-flex mb-2 bg-white rounded border p-1 shadow-sm flex-shrink-0">
                    <button
                      type="button"
                      className={`btn btn-sm flex-grow-1 fw-bold d-flex align-items-center justify-content-center ${archiveRightPanelMode === 'map' ? 'btn-primary' : 'btn-light'}`}
                      onClick={() => setArchiveRightPanelMode('map')}
                      style={{ transition: 'all 0.2s' }}
                    >
                      <CIcon icon={cilMap} className="me-2" /> Карта маршрута
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm flex-grow-1 fw-bold d-flex align-items-center justify-content-center ${archiveRightPanelMode === 'archive' ? 'btn-primary' : 'btn-light'}`}
                      onClick={() => setArchiveRightPanelMode('archive')}
                      style={{ transition: 'all 0.2s' }}
                    >
                      <CIcon icon={cilFolderOpen} className="me-2" /> Файлы архива
                    </button>
                  </div>

                  <div className="flex-grow-1 d-flex flex-column overflow-hidden position-relative modal-side-content">
                    {archiveRightPanelMode === 'map' ? (
                      <CCard className="h-100 border-0 shadow-sm w-100 overflow-hidden">
                        <GPSMap
                          trajectory={trajectoryData}
                          currentTime={currentAbsoluteTime ? currentAbsoluteTime.getTime() : clickedTimeMs}
                          sites={analyticsData?.sites_summary || []}
                          onSiteClick={(site) => {
                            handleTimeDoubleClick({ exactTime: new Date(site.start).getTime() });
                          }}
                          height="100%"
                        />
                      </CCard>
                    ) : (
                      <CameraArchiveList
                        t={t}
                        isStaff={false}
                        folders={folders}
                        selectedVideo={selectedVideo}
                        handleVideoSelect={handleVideoSelect}
                        timezoneOffset={device?.timezone_offset || 0}
                      />
                    )}
                  </div>
                </div>
              </div>

              {analyticsData && (
                <div className="mt-2 flex-shrink-0 modal-timeline-wrapper">
                  <GarbageTimelineSection
                    dateRangeForVis={dateRangeForVis}
                    statusTimelineItems={statusTimelineItems}
                    sitesTimelineItems={sitesTimelineItems}
                    handleTimeDoubleClick={handleTimeDoubleClick}
                    currentTimeMs={currentAbsoluteTime ? currentAbsoluteTime.getTime() : clickedTimeMs}
                  />
                </div>
              )}
            </div>
          </CModalBody>
        </CModal>

        {/* МОДАЛЬНОЕ ОКНО ПЕРЕСЧЕТА */}
        <CModal
          visible={recalcModalVisible}
          onClose={() => setRecalcModalVisible(false)}
        >
          <CModalHeader onClose={() => setRecalcModalVisible(false)}>
            <CModalTitle>Пересчитать всю аналитику мусоровоза</CModalTitle>
          </CModalHeader>
          <CModalBody>
            <p>
              Выберите дату и время, с которого нужно начать полный пересчет площадок ТКО. Все данные после этой точки будут очищены и пересчитаны воркером заново.
            </p>
            <DatePicker
              selected={recalcStartDate}
              onChange={setRecalcStartDate}
              className="form-control"
              showTimeSelect
              timeCaption="Время"
              dateFormat="dd.MM.yyyy HH:mm"
              locale="ru"
              includeDates={availableDates && availableDates.length > 0 ? availableDates : undefined}
            />
          </CModalBody>
          <CModalFooter>
            <CButton
              color="secondary"
              onClick={() => setRecalcModalVisible(false)}
            >
              Отмена
            </CButton>
            <CButton
              color="primary"
              onClick={handleRecalculateSubmit}
              disabled={recalculating}
            >
              {recalculating ? <CSpinner size="sm" /> : "Начать пересчет"}
            </CButton>
          </CModalFooter>
        </CModal>

        {/* МОДАЛЬНОЕ ОКНО ФОРМАТИРОВАНИЯ И ВОССТАНОВЛЕНИЯ ВИДИМОСТИ */}
        <CameraModals
          device={device}
          isStaff={isStaff}
          recalcModalVisible={false}
          setRecalcModalVisible={() => {}}
          formatModalVisible={formatModalVisible}
          setFormatModalVisible={setFormatModalVisible}
          formatMode={formatMode}
          setFormatMode={setFormatMode}
          formatDate={formatDate}
          setFormatDate={setFormatDate}
          formatConfirmation={formatConfirmation}
          setFormatConfirmation={setFormatConfirmation}
          formatActionTab={formatActionTab}
          setFormatActionTab={setFormatActionTab}
          formatStats={formatStats}
          handleClearData={handleClearData}
          handleRestoreData={handleRestoreData}
          formatting={formatting}
        />
      </CContainer>
    </div>
  );
};

export default GarbageTruckDetailPage;